/* eslint-disable react/prop-types */
import React, { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import MobileNavigation from './MobileNavigation';
import DesktopNavigation from './DesktopNavigation';
import Logo from '../../assets/logo/Logo.png';
import LogoLight from '../../assets/logo/logo-dark.png';
import AppTour from '../AppTour/AppTour';
import useAppTour from '../../hooks/useAppTour';
import useTheme from '../../hooks/useTheme';
import { useAppStore } from '../../store/useAppStore';
import { useOnboardingStore } from '../../store/useOnboardingStore';
import { useAppTourStore } from '../../store/useAppTourStore';
import { getRedirectUrl } from '../../utils/helpers';

export const withNavigation = (WrappedComponent) => {
  return function NavigationLayout(props) {
    const { navigation = [], identity, onLogout, children, ...rest } = props;

    const location = useLocation();
    const navigate = useNavigate();
    const contactSupportUrl = getRedirectUrl('dashboard/support/contact');

    const { startTour } = useAppTour();
    const { completedTours } = useAppTourStore();
    const { shouldShowGuide, setShouldShowGuide, flowState } = useOnboardingStore();
    const { sidebarOpen, setSidebarOpen } = useAppStore();

    // Initialise theme on mount (reads localStorage / system preference)
    const { isDark } = useTheme();

    const handleStartTour = useCallback(() => {
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      if (!sidebarOpen) {
        setSidebarOpen(true);
      }
      startTour();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sidebarOpen, setSidebarOpen, startTour, location.pathname]);

    const isCurrentPath = useCallback(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
      [location.pathname]
    );

    useEffect(() => {
      if (!shouldShowGuide) return;
      const hasTourBeenCompleted = completedTours.includes('default');
      if (shouldShowGuide && flowState === 'completed' && !hasTourBeenCompleted) {
        if (!sidebarOpen) setSidebarOpen(true);
        startTour();
        setShouldShowGuide(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShowGuide, flowState, startTour, sidebarOpen, completedTours]);

    if (location.pathname === '/test-page') {
      return <>{children}</>;
    }

    return (
      <div className='app-shell'>
        <AppTour />

        <MobileNavigation
          navigation={navigation}
          isCurrentPath={isCurrentPath}
          identity={identity}
          onLogout={onLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onStartTour={handleStartTour}
          contactSupportUrl={contactSupportUrl}
        />

        <DesktopNavigation
          navigation={navigation}
          isCurrentPath={isCurrentPath}
          identity={identity}
          onLogout={onLogout}
          onStartTour={handleStartTour}
          contactSupportUrl={contactSupportUrl}
        />

        {/* Main content area */}
        <div className='flex flex-col h-screen transition-all duration-300 lg:pl-64'>
          {/* Mobile top-bar */}
          <div
            className='sticky top-0 z-20 flex h-14 items-center justify-between px-5 lg:hidden'
            style={{
              background: 'var(--sidebar-bg)',
              borderBottom: '1px solid var(--sidebar-border)',
            }}
          >
            {/* Mobile logo */}
            <img src={isDark ? Logo : LogoLight} alt='Postreams' className='h-8 w-auto' />

            <button
              type='button'
              aria-label='Open menu'
              onClick={() => setSidebarOpen(true)}
              className='flex items-center justify-center w-9 h-9 rounded-xl cursor-pointer transition-colors'
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
            >
              <Menu style={{ width: '1.125rem', height: '1.125rem' }} />
            </button>
          </div>

          {/* Page content */}
          <main className='flex-grow overflow-y-auto thin-scrollbar' style={{ background: 'var(--bg-base)' }}>
            <div
              className='container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-7xl'
              data-tour='main-content'
            >
              <WrappedComponent {...rest}>{children}</WrappedComponent>
            </div>
          </main>
        </div>
      </div>
    );
  };
};

export default withNavigation;
